import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { getImageUrl } from './imageHelper';
import { 
  X, Plus, Edit2, Trash2, Save, Calendar, Tag, 
  Check, Search, Package, Grid3x3, Layers, ShoppingBag, 
  Box, Wallet, Sparkles
} from 'lucide-react';

/**
 * ========================================
 * PROMOTIONS ADMIN - SIMPLIFIED
 * ========================================
 */

const PromotionsAdmin = () => {
  const [promotions, setPromotions] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [variantFamilies, setVariantFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);

  useEffect(() => {
    fetchPromotions();
    fetchProducts();
    fetchCategories();
    fetchVariantFamilies();
  }, []);

  const fetchPromotions = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('promotions_deals')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setPromotions(data || []);
    } catch (error) {
      console.error('Error fetching promotions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      let allProducts = [];
      let from = 0;
      const batchSize = 1000;
      let hasMore = true;

      while (hasMore) {
        const { data, error, count } = await supabase
          .from('products')
          .select('id, name, price, image, stock_code, category, published, unit', { count: 'exact' })
          .eq('published', true)
          .order('name')
          .range(from, from + batchSize - 1);

        if (error) throw error;
        
        if (data && data.length > 0) {
          allProducts = [...allProducts, ...data];
          from += batchSize;
          // Continue if we got a full batch (might be more)
          hasMore = data.length === batchSize;
        } else {
          hasMore = false;
        }
        
        // Log for debugging
        console.log(`📦 Fetched batch: ${data?.length || 0} products, total so far: ${allProducts.length}, count from DB: ${count}`);
      }
      
      setProducts(allProducts);
      console.log(`✅ Total products loaded: ${allProducts.length}`);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase.from('categories').select('*').order('name');
      if (error) throw error;
      setCategories(data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchVariantFamilies = async () => {
    try {
      const { data, error } = await supabase.from('product_variant_config').select('*').order('family_name');
      if (error) throw error;
      setVariantFamilies(data || []);
    } catch (error) {
      console.error('Error fetching variant families:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this promotion?')) return;
    try {
      await supabase.from('promotions_deals').delete().eq('id', id);
      fetchPromotions();
    } catch (error) {
      alert('Failed to delete');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Promotions & Deals</h1>
            <p className="text-gray-600 mt-1">Create and manage promotional campaigns</p>
          </div>
          <button
            onClick={() => { setEditingPromotion(null); setShowModal(true); }}
            className="flex items-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Plus size={20} />
            Create Promotion
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {promotions.map(promo => (
              <PromotionCard 
                key={promo.id} 
                promotion={promo}
                onEdit={() => { setEditingPromotion(promo); setShowModal(true); }}
                onDelete={() => handleDelete(promo.id)}
              />
            ))}
          </div>
        )}

        {showModal && (
          <PromotionModal
            promotion={editingPromotion}
            products={products}
            categories={categories}
            variantFamilies={variantFamilies}
            onClose={() => { setShowModal(false); setEditingPromotion(null); }}
            onSuccess={() => { fetchPromotions(); setShowModal(false); setEditingPromotion(null); }}
          />
        )}
      </div>
    </div>
  );
};

// ========================================
// PROMOTION CARD
// ========================================
const PromotionCard = ({ promotion, onEdit, onDelete }) => {
  const now = new Date();
  const startDate = new Date(promotion.start_date);
  const endDate = new Date(promotion.end_date);
  const isActive = promotion.is_active && now >= startDate && now <= endDate;
  const hasEnded = now > endDate;

  const getTypeLabel = () => {
    const labels = {
      all: '🌐 All Products',
      category: '📂 Category',
      specific: '📦 Specific Products',
      variant_family: '🔗 Variant Family',
      combo: '📦 COMBO Bundle',
      credit_wallet: '💰 Loyalty Cashback',
      all_in_one: '⭐ All-In-One'
    };
    return labels[promotion.applies_to] || promotion.applies_to;
  };

  const getSummary = () => {
    if (promotion.applies_to === 'combo') {
      return `Buy ${promotion.combo_quantity || 3} for R${parseFloat(promotion.combo_price || 0).toFixed(2)}`;
    }
    if (promotion.applies_to === 'all_in_one' && promotion.all_in_one_config) {
      const config = typeof promotion.all_in_one_config === 'string' 
        ? JSON.parse(promotion.all_in_one_config) 
        : promotion.all_in_one_config;
      const parts = [];
      if (config.combo_products?.length > 0) parts.push(`${config.combo_products.length} combo`);
      if (config.markdown_count > 0) parts.push(`${config.markdown_count} markdown`);
      if (config.cashback_count > 0) parts.push(`${config.cashback_count} cashback`);
      return parts.join(' • ') || 'Not configured';
    }
    return null;
  };

  return (
    <div className={`bg-white rounded-lg shadow-md overflow-hidden border-2 ${
      promotion.applies_to === 'all_in_one' ? 'border-purple-300' : 'border-gray-200'
    } hover:border-green-500 transition-colors`}>
      <div className={`px-4 py-2 ${
        promotion.applies_to === 'all_in_one' 
          ? 'bg-gradient-to-r from-purple-600 to-pink-600'
          : isActive ? 'bg-green-500' : hasEnded ? 'bg-gray-400' : 'bg-yellow-500'
      }`}>
        <p className="text-white text-sm font-semibold">
          {isActive ? '🟢 ACTIVE' : hasEnded ? '⚫ ENDED' : '🟡 SCHEDULED'}
        </p>
      </div>

      <div className="p-4">
        <h3 className="text-lg font-bold text-gray-900 mb-2">{promotion.name}</h3>
        <p className="text-sm text-gray-600 mb-3">{promotion.description}</p>

        <div className="space-y-1 text-sm text-gray-600 mb-4">
          <div className="flex items-center gap-2">
            <Calendar size={14} />
            {new Date(promotion.start_date).toLocaleDateString()} - {new Date(promotion.end_date).toLocaleDateString()}
          </div>
          <div className="flex items-center gap-2">
            <Tag size={14} />
            {getTypeLabel()}
          </div>
          {getSummary() && (
            <div className="bg-gray-50 rounded px-2 py-1 mt-2 text-xs font-medium">{getSummary()}</div>
          )}
        </div>

        <div className="flex gap-2">
          <button onClick={onEdit} className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100">
            <Edit2 size={16} /> Edit
          </button>
          <button onClick={onDelete} className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100">
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

// ========================================
// ALL-IN-ONE MODAL WITH MULTIPLE COMBOS
// ========================================
const AllInOneModal = ({ promotion, products, variantFamilies, onClose, onSuccess }) => {
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Basic info
  const [name, setName] = useState(promotion?.name || '');
  const [description, setDescription] = useState(promotion?.description || '');
  const [startDate, setStartDate] = useState(
    promotion?.start_date?.split('T')[0] || new Date().toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(
    promotion?.end_date?.split('T')[0] || new Date(Date.now() + 7*24*60*60*1000).toISOString().split('T')[0]
  );
  
  // Banner style selection
  const [bannerStyle, setBannerStyle] = useState('red');
  
  // Banner style options - Solid Colors
  const bannerStyles = [
    { key: 'red', name: 'Red Hot', colors: 'from-red-500 to-rose-600', textColor: 'text-white', icon: '🔥', type: 'gradient' },
    { key: 'orange', name: 'Orange Combo', colors: 'from-orange-500 to-amber-500', textColor: 'text-white', icon: '📦', type: 'gradient' },
    { key: 'green', name: 'Fresh Green', colors: 'from-green-500 to-emerald-600', textColor: 'text-white', icon: '🌿', type: 'gradient' },
    { key: 'blue', name: 'Cool Blue', colors: 'from-blue-500 to-indigo-600', textColor: 'text-white', icon: '❄️', type: 'gradient' },
    { key: 'purple', name: 'Premium Purple', colors: 'from-purple-500 to-pink-600', textColor: 'text-white', icon: '⭐', type: 'gradient' },
    { key: 'gold', name: 'Gold Special', colors: 'from-yellow-400 to-amber-500', textColor: 'text-gray-900', icon: '🏆', type: 'gradient' },
    { key: 'pink', name: 'Pink Flash', colors: 'from-pink-500 to-rose-500', textColor: 'text-white', icon: '💖', type: 'gradient' },
    { key: 'teal', name: 'Teal Deal', colors: 'from-teal-500 to-cyan-600', textColor: 'text-white', icon: '🎯', type: 'gradient' },
  ];
  
  // Themed image banners
  const themedBanners = [
    { 
      key: 'back_to_school', 
      name: 'Back to School', 
      icon: '📚',
      type: 'image',
      image: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=1200&q=80',
      overlay: 'from-blue-900/80 to-blue-600/60'
    },
    { 
      key: 'christmas', 
      name: 'Christmas', 
      icon: '🎄',
      type: 'image',
      image: 'https://images.unsplash.com/photo-1512389142860-9c449e58a814?w=1200&q=80',
      overlay: 'from-red-900/80 to-green-800/60'
    },
    { 
      key: 'summer', 
      name: 'Summer Vibes', 
      icon: '☀️',
      type: 'image',
      image: 'https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=1200&q=80',
      overlay: 'from-orange-600/70 to-yellow-500/50'
    },
    { 
      key: 'fresh_produce', 
      name: 'Fresh & Healthy', 
      icon: '🥗',
      type: 'image',
      image: 'https://images.unsplash.com/photo-1610832958506-aa56368176cf?w=1200&q=80',
      overlay: 'from-green-900/80 to-green-600/60'
    },
    { 
      key: 'cleaning', 
      name: 'Cleaning Deals', 
      icon: '🧹',
      type: 'image',
      image: 'https://images.unsplash.com/photo-1563453392212-326f5e854473?w=1200&q=80',
      overlay: 'from-cyan-800/80 to-blue-600/60'
    },
    { 
      key: 'baby', 
      name: 'Baby Essentials', 
      icon: '👶',
      type: 'image',
      image: 'https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?w=1200&q=80',
      overlay: 'from-pink-700/80 to-purple-500/60'
    },
    { 
      key: 'pet', 
      name: 'Pet Lovers', 
      icon: '🐕',
      type: 'image',
      image: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=1200&q=80',
      overlay: 'from-amber-800/80 to-orange-600/60'
    },
    { 
      key: 'braai', 
      name: 'Braai Day', 
      icon: '🔥',
      type: 'image',
      image: 'https://images.unsplash.com/photo-1555939594-58d7cb561ad1?w=1200&q=80',
      overlay: 'from-red-900/80 to-orange-700/60'
    },
    { 
      key: 'easter', 
      name: 'Easter Special', 
      icon: '🐰',
      type: 'image',
      image: 'https://images.unsplash.com/photo-1521967906867-14ec9d64bee8?w=1200&q=80',
      overlay: 'from-purple-700/80 to-pink-500/60'
    },
    { 
      key: 'mothers_day', 
      name: "Mother's Day", 
      icon: '💐',
      type: 'image',
      image: 'https://images.unsplash.com/photo-1462275646964-a0e3571f4f5f?w=1200&q=80',
      overlay: 'from-pink-800/80 to-rose-500/60'
    },
    { 
      key: 'fathers_day', 
      name: "Father's Day", 
      icon: '👔',
      type: 'image',
      image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1200&q=80',
      overlay: 'from-slate-800/80 to-blue-700/60'
    },
    { 
      key: 'new_year', 
      name: 'New Year', 
      icon: '🎆',
      type: 'image',
      image: 'https://images.unsplash.com/photo-1467810563316-b5476525c0f9?w=1200&q=80',
      overlay: 'from-indigo-900/80 to-purple-600/60'
    },
  ];
  
  // Combined banner options
  const allBannerOptions = [...bannerStyles, ...themedBanners];
  const selectedBanner = allBannerOptions.find(b => b.key === bannerStyle) || bannerStyles[0];
  
  // Multiple combo deals: [{ id, name, quantity, price, sameItem, variantFamilyId }]
  const [combos, setCombos] = useState([]);
  const [showComboForm, setShowComboForm] = useState(false);
  const [editingCombo, setEditingCombo] = useState(null);
  const [comboForm, setComboForm] = useState({ name: '', quantity: 3, price: '', sameItem: false, variantFamilyId: null });
  
  // Product column width for resizable column
  const [productColWidth, setProductColWidth] = useState(null);
  const [isResizing, setIsResizing] = useState(false);
  
  // Product configs: { productId: { comboId: string|null, salePrice: '', cashback: '' } }
  const [productConfigs, setProductConfigs] = useState({});

  // Load existing promotion
  useEffect(() => {
    if (promotion?.applies_to === 'all_in_one' && promotion?.all_in_one_config) {
      const config = typeof promotion.all_in_one_config === 'string' 
        ? JSON.parse(promotion.all_in_one_config) 
        : promotion.all_in_one_config;
      
      setCombos(config.combos || []);
      setBannerStyle(config.banner_style || 'red');
      
      const configs = {};
      // Load combo assignments
      Object.entries(config.product_combos || {}).forEach(([id, comboId]) => {
        configs[id] = { ...configs[id], comboId };
      });
      // Load markdowns
      Object.entries(config.markdown_prices || {}).forEach(([id, price]) => { 
        configs[id] = { ...configs[id], salePrice: String(price) }; 
      });
      // Load cashback
      Object.entries(config.cashback_amounts || {}).forEach(([id, amt]) => { 
        configs[id] = { ...configs[id], cashback: String(amt) }; 
      });
      setProductConfigs(configs);
    }
  }, [promotion]);

  const categories = [...new Set(products.map(p => p.category).filter(Boolean))].sort();

  // Handle column resize
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
    const startX = e.clientX;
    const startWidth = productColWidth || 280;
    
    const handleMouseMove = (e) => {
      const newWidth = Math.max(200, Math.min(500, startWidth + (e.clientX - startX)));
      setProductColWidth(newWidth);
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  const filteredProducts = products.filter(product => {
    const searchLower = search.toLowerCase().trim();
    const matchesSearch = !searchLower || 
      product.name?.toLowerCase().includes(searchLower) ||
      String(product.stock_code || '').toLowerCase().includes(searchLower);
    const matchesCategory = categoryFilter === 'all' || product.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Count products per combo
  const getComboProductCount = (comboId) => {
    return Object.values(productConfigs).filter(c => c.comboId === comboId).length;
  };

  // Summary counts
  const productsWithCombo = Object.entries(productConfigs).filter(([_, c]) => c.comboId);
  const productsWithMarkdown = Object.entries(productConfigs).filter(([_, c]) => c.salePrice && parseFloat(c.salePrice) > 0);
  const productsWithCashback = Object.entries(productConfigs).filter(([_, c]) => c.cashback && parseFloat(c.cashback) > 0);
  const totalCashback = productsWithCashback.reduce((sum, [_, c]) => sum + (parseFloat(c.cashback) || 0), 0);

  const updateConfig = (productId, field, value) => {
    setProductConfigs(prev => ({
      ...prev,
      [productId]: { ...prev[productId], [field]: value }
    }));
  };

  // Combo CRUD
  const addCombo = () => {
    if (!comboForm.name.trim()) { alert('Enter combo name'); return; }
    if (!comboForm.price || parseFloat(comboForm.price) <= 0) { alert('Enter valid price'); return; }
    
    // If Mix & Match checkbox is checked but no family selected
    if (comboForm.variantFamilyId === '') { 
      alert('Please select a variant family for Mix & Match'); 
      return; 
    }
    
    const newCombo = {
      id: editingCombo ? editingCombo.id : `combo_${Date.now()}`,
      name: comboForm.name,
      quantity: parseInt(comboForm.quantity) || 2,
      price: parseFloat(comboForm.price),
      sameItem: comboForm.sameItem,
      variantFamilyId: comboForm.variantFamilyId || null
    };
    
    if (editingCombo) {
      setCombos(prev => prev.map(c => c.id === editingCombo.id ? newCombo : c));
    } else {
      setCombos(prev => [...prev, newCombo]);
    }
    
    // If Mix & Match is enabled, auto-assign all products from the variant family
    if (comboForm.variantFamilyId) {
      const family = variantFamilies.find(f => f.id === comboForm.variantFamilyId);
      if (family && family.product_ids) {
        setProductConfigs(prev => {
          const updated = { ...prev };
          family.product_ids.forEach(productId => {
            updated[productId] = { 
              ...updated[productId], 
              comboId: newCombo.id 
            };
          });
          return updated;
        });
      }
    }
    
    setComboForm({ name: '', quantity: 3, price: '', sameItem: false, variantFamilyId: null });
    setShowComboForm(false);
    setEditingCombo(null);
  };

  const deleteCombo = (comboId) => {
    // Remove combo and clear assignments
    setCombos(prev => prev.filter(c => c.id !== comboId));
    setProductConfigs(prev => {
      const updated = { ...prev };
      Object.keys(updated).forEach(productId => {
        if (updated[productId]?.comboId === comboId) {
          updated[productId] = { ...updated[productId], comboId: null };
        }
      });
      return updated;
    });
  };

  const editCombo = (combo) => {
    setComboForm({
      name: combo.name,
      quantity: combo.quantity,
      price: String(combo.price),
      sameItem: combo.sameItem,
      variantFamilyId: combo.variantFamilyId || null
    });
    setEditingCombo(combo);
    setShowComboForm(true);
  };

  const handleSave = async () => {
    if (!name.trim()) { alert('Please enter a promotion name'); return; }
    
    // Validate combos have products
    for (const combo of combos) {
      const count = getComboProductCount(combo.id);
      if (count === 0) {
        alert(`Combo "${combo.name}" has no products assigned`);
        return;
      }
    }

    if (combos.length === 0 && productsWithMarkdown.length === 0 && productsWithCashback.length === 0) {
      alert('Please add at least one combo, markdown, or cashback');
      return;
    }

    setSaving(true);
    try {
      const config = {
        combos: combos,
        banner_style: bannerStyle,
        product_combos: Object.fromEntries(
          productsWithCombo.map(([id, c]) => [id, c.comboId])
        ),
        markdown_prices: Object.fromEntries(
          productsWithMarkdown.map(([id, c]) => [id, parseFloat(c.salePrice) || 0])
        ),
        markdown_count: productsWithMarkdown.length,
        cashback_amounts: Object.fromEntries(
          productsWithCashback.map(([id, c]) => [id, parseFloat(c.cashback) || 0])
        ),
        cashback_count: productsWithCashback.length,
        total_cashback: totalCashback,
      };

      const allProductIds = [...new Set([
        ...productsWithCombo.map(([id]) => id),
        ...productsWithMarkdown.map(([id]) => id),
        ...productsWithCashback.map(([id]) => id),
      ])];

      const promoData = {
        name,
        description,
        promotion_type: 'daily',
        applies_to: 'all_in_one',
        start_date: startDate,
        end_date: endDate,
        is_active: true,
        product_ids: allProductIds,
        all_in_one_config: config,
        discount_type: 'markdown',
        updated_at: new Date().toISOString(),
      };

      if (promotion) {
        const { error } = await supabase.from('promotions_deals').update(promoData).eq('id', promotion.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('promotions_deals').insert([promoData]);
        if (error) throw error;
      }
      
      alert('✅ Promotion saved!');
      onSuccess();
    } catch (error) {
      alert('Failed to save: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2">
      <div className="bg-white w-full max-w-6xl h-[95vh] rounded-xl flex flex-col overflow-hidden">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-4 flex items-center justify-between shrink-0">
          <div className="flex items-center gap-3">
            <Sparkles size={28} />
            <div>
              <h2 className="text-xl font-bold">{promotion ? 'Edit' : 'Create'} All-In-One Promotion</h2>
              <p className="text-purple-100 text-sm">Multiple Combos + Markdowns + Cashback</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
            <X size={24} />
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          
          {/* Left Sidebar */}
          <div className="w-80 bg-gray-50 p-4 border-r overflow-y-auto shrink-0">
            
            {/* Basic Info */}
            <div className="space-y-3 mb-6">
              <h3 className="font-bold text-gray-800 text-sm uppercase tracking-wide">Basic Info</h3>
              <input
                type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Promotion Name *"
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm"
              />
              <textarea
                value={description} onChange={(e) => setDescription(e.target.value)}
                placeholder="Description" rows={2}
                className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm resize-none"
              />
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500">Start</label>
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">End</label>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm" />
                </div>
              </div>
              
              {/* Banner Style Selection */}
              <div className="mt-3">
                <label className="text-xs text-gray-500 block mb-2">Banner Style</label>
                
                {/* Solid Color Gradients */}
                <p className="text-xs font-medium text-gray-600 mb-1">Solid Colors</p>
                <div className="grid grid-cols-2 gap-1.5 mb-3">
                  {bannerStyles.map(style => (
                    <button
                      key={style.key}
                      type="button"
                      onClick={() => setBannerStyle(style.key)}
                      className={`p-1.5 rounded-lg text-xs font-medium transition-all ${
                        bannerStyle === style.key 
                          ? `bg-gradient-to-r ${style.colors} ${style.textColor} ring-2 ring-offset-1 ring-gray-400 scale-105` 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <span className="mr-1">{style.icon}</span>
                      {style.name}
                    </button>
                  ))}
                </div>
                
                {/* Themed Image Banners */}
                <p className="text-xs font-medium text-gray-600 mb-1">Themed Images</p>
                <div className="grid grid-cols-2 gap-1.5">
                  {themedBanners.map(theme => (
                    <button
                      key={theme.key}
                      type="button"
                      onClick={() => setBannerStyle(theme.key)}
                      className={`p-1.5 rounded-lg text-xs font-medium transition-all ${
                        bannerStyle === theme.key 
                          ? 'bg-purple-600 text-white ring-2 ring-offset-1 ring-purple-400 scale-105' 
                          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      }`}
                    >
                      <span className="mr-1">{theme.icon}</span>
                      {theme.name}
                    </button>
                  ))}
                </div>
                
                {/* Preview */}
                <div className="mt-3 rounded-lg overflow-hidden shadow-md">
                  {selectedBanner.type === 'image' ? (
                    <div 
                      className="relative h-24"
                      style={{
                        backgroundImage: `url(${selectedBanner.image})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    >
                      <div className={`absolute inset-0 bg-gradient-to-r ${selectedBanner.overlay}`}></div>
                      <div className="relative p-3 text-white">
                        <p className="text-xs font-bold opacity-80">Banner Preview</p>
                        <p className="text-sm font-bold">{name || 'Your Promotion Name'}</p>
                        <p className="text-xs opacity-75 mt-0.5">{selectedBanner.name} Theme</p>
                      </div>
                    </div>
                  ) : (
                    <div className={`p-3 bg-gradient-to-r ${selectedBanner.colors} ${selectedBanner.textColor}`}>
                      <p className="text-xs font-bold opacity-80">Banner Preview</p>
                      <p className="text-sm font-bold">{name || 'Your Promotion Name'}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Combo Deals Section */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-orange-800 text-sm flex items-center gap-2">
                  <Box size={16} /> Combo Deals
                </h3>
                <button
                  onClick={() => { setShowComboForm(true); setEditingCombo(null); setComboForm({ name: '', quantity: 3, price: '', sameItem: false, variantFamilyId: null }); }}
                  className="text-xs bg-orange-500 text-white px-2 py-1 rounded hover:bg-orange-600 flex items-center gap-1"
                >
                  <Plus size={12} /> Add
                </button>
              </div>

              {/* Combo Form */}
              {showComboForm && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3 space-y-2">
                  <input
                    type="text" value={comboForm.name} onChange={(e) => setComboForm({ ...comboForm, name: e.target.value })}
                    placeholder="Combo name (e.g., Cheese Deal)"
                    className="w-full px-2 py-1.5 border rounded text-sm"
                  />
                  <div className="flex items-center gap-2 text-sm">
                    <span>Buy</span>
                    <input
                      type="number" min="2" value={comboForm.quantity}
                      onChange={(e) => setComboForm({ ...comboForm, quantity: parseInt(e.target.value) || 2 })}
                      className="w-12 px-1 py-1 border rounded text-center"
                    />
                    <span>for R</span>
                    <input
                      type="text" inputMode="decimal" value={comboForm.price}
                      onChange={(e) => setComboForm({ ...comboForm, price: e.target.value })}
                      placeholder="0" className="w-16 px-1 py-1 border rounded"
                    />
                  </div>
                  <label className="flex items-center gap-2 text-xs cursor-pointer">
                    <input type="checkbox" checked={comboForm.sameItem}
                      onChange={(e) => setComboForm({ ...comboForm, sameItem: e.target.checked, variantFamilyId: null })} className="rounded" />
                    <span>Same item only</span>
                  </label>
                  
                  {/* Mix & Match Variant Family Option */}
                  <div className="border-t border-orange-200 pt-2 mt-2">
                    <label className="flex items-center gap-2 text-xs cursor-pointer mb-2">
                      <input 
                        type="checkbox" 
                        checked={!!comboForm.variantFamilyId}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setComboForm({ ...comboForm, sameItem: false, variantFamilyId: '' });
                          } else {
                            setComboForm({ ...comboForm, variantFamilyId: null });
                          }
                        }} 
                        className="rounded" 
                      />
                      <span className="font-medium text-purple-700">🔀 Mix & Match (Variant Family)</span>
                    </label>
                    
                    {comboForm.variantFamilyId !== null && (
                      <div className="bg-purple-50 border border-purple-200 rounded p-2">
                        <p className="text-xs text-purple-600 mb-1">Customer can pick ANY products from this family:</p>
                        <select
                          value={comboForm.variantFamilyId || ''}
                          onChange={(e) => {
                            const familyId = e.target.value;
                            const family = variantFamilies.find(f => f.id === familyId);
                            if (family && !comboForm.name) {
                              // Auto-fill name from family name if empty
                              setComboForm({ 
                                ...comboForm, 
                                variantFamilyId: familyId,
                                name: comboForm.name || `${family.family_name} Mix & Match`
                              });
                            } else {
                              setComboForm({ ...comboForm, variantFamilyId: familyId });
                            }
                          }}
                          className="w-full px-2 py-1.5 border rounded text-sm"
                        >
                          <option value="">Select variant family...</option>
                          {variantFamilies.map(family => (
                            <option key={family.id} value={family.id}>
                              {family.family_name} ({family.product_ids?.length || 0} variants)
                            </option>
                          ))}
                        </select>
                        {comboForm.variantFamilyId && (
                          <p className="text-xs text-green-600 mt-1">
                            ✓ {variantFamilies.find(f => f.id === comboForm.variantFamilyId)?.product_ids?.length || 0} products will be auto-assigned
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="flex gap-2">
                    <button onClick={addCombo} className="flex-1 bg-orange-500 text-white py-1.5 rounded text-xs font-medium hover:bg-orange-600">
                      {editingCombo ? 'Update' : 'Add'} Combo
                    </button>
                    <button onClick={() => { setShowComboForm(false); setEditingCombo(null); }}
                      className="px-3 py-1.5 border rounded text-xs hover:bg-gray-100">Cancel</button>
                  </div>
                </div>
              )}

              {/* Combo List */}
              {combos.length === 0 ? (
                <p className="text-xs text-gray-500 italic">No combo deals yet</p>
              ) : (
                <div className="space-y-2">
                  {combos.map(combo => {
                    const variantFamily = combo.variantFamilyId 
                      ? variantFamilies.find(f => f.id === combo.variantFamilyId) 
                      : null;
                    return (
                    <div key={combo.id} className={`bg-white border rounded-lg p-2 ${combo.variantFamilyId ? 'border-purple-300' : 'border-orange-200'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-sm text-orange-800">{combo.name}</p>
                          <p className="text-xs text-gray-600">
                            Buy {combo.quantity} for R{combo.price.toFixed(2)}
                            {combo.sameItem && <span className="text-orange-600"> • Same item</span>}
                            {combo.variantFamilyId && <span className="text-purple-600"> • 🔀 Mix & Match</span>}
                          </p>
                          {variantFamily && (
                            <p className="text-xs text-purple-500">Family: {variantFamily.family_name}</p>
                          )}
                          <p className="text-xs text-gray-500">{getComboProductCount(combo.id)} products</p>
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => editCombo(combo)} className="p-1 text-blue-500 hover:bg-blue-50 rounded">
                            <Edit2 size={14} />
                          </button>
                          <button onClick={() => deleteCombo(combo.id)} className="p-1 text-red-500 hover:bg-red-50 rounded">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </div>
                  )})}
                </div>
              )}
            </div>

            {/* Summary */}
            <div className="bg-white rounded-lg p-3 border shadow-sm">
              <h3 className="font-bold text-gray-800 text-sm mb-2">Summary</h3>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-orange-600 flex items-center gap-1"><Box size={12}/> Combos</span>
                  <span className="font-bold">{combos.length} ({productsWithCombo.length} products)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-green-600 flex items-center gap-1"><Tag size={12}/> Markdowns</span>
                  <span className="font-bold">{productsWithMarkdown.length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-purple-600 flex items-center gap-1"><Wallet size={12}/> Cashback</span>
                  <span className="font-bold">{productsWithCashback.length}</span>
                </div>
                {totalCashback > 0 && (
                  <div className="pt-1.5 border-t flex justify-between text-purple-700">
                    <span>Total Cashback</span>
                    <span className="font-bold">R{totalCashback.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right Panel - Products */}
          <div className="flex-1 flex flex-col overflow-hidden">
            
            {/* Search Bar */}
            <div className="p-3 bg-white border-b flex gap-2 shrink-0">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by name or code..."
                  className="w-full pl-10 pr-4 py-2 border-2 border-gray-300 rounded-lg" />
              </div>
              <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}
                className="px-3 py-2 border-2 border-gray-300 rounded-lg bg-white">
                <option value="all">All Categories</option>
                {categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
              </select>
            </div>

            {/* Table Header */}
            <div className="bg-gray-100 px-4 py-2 flex text-xs font-bold text-gray-600 uppercase shrink-0 border-b select-none">
              <div 
                className="flex items-center gap-1" 
                style={{ width: productColWidth || 280, minWidth: 200, flexShrink: 0 }}
              >
                <span>Product</span>
                {/* Resize Handle */}
                <div
                  onMouseDown={handleMouseDown}
                  className={`ml-auto w-1 h-full cursor-col-resize hover:bg-purple-400 ${isResizing ? 'bg-purple-500' : 'bg-gray-300'}`}
                  style={{ minHeight: 20, marginRight: 8 }}
                  title="Drag to resize column"
                />
              </div>
              <div className="flex-1 flex">
                <div className="flex-1 text-center text-orange-600">Combo Deal</div>
                <div className="w-28 text-center text-green-600">Sale Price</div>
                <div className="w-32 text-center text-purple-600">Cashback</div>
              </div>
            </div>

            {/* Product List */}
            <div className="flex-1 overflow-y-auto">
              {filteredProducts.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No products found</div>
              ) : (
                filteredProducts.map(product => {
                  const config = productConfigs[product.id] || {};
                  const salePrice = parseFloat(config.salePrice) || 0;
                  const savings = salePrice > 0 ? product.price - salePrice : 0;
                  const hasConfig = config.comboId || config.salePrice || config.cashback;
                  const selectedCombo = combos.find(c => c.id === config.comboId);
                  
                  return (
                    <div key={product.id} className={`px-4 py-2.5 flex items-center border-b hover:bg-gray-50 ${hasConfig ? 'bg-purple-50/30' : ''}`}>
                      
                      {/* Product */}
                      <div 
                        className="flex items-center gap-3 flex-shrink-0 pr-2" 
                        style={{ width: productColWidth || 280, minWidth: 200 }}
                      >
                        <img src={getImageUrl(product.image)} alt="" className="w-10 h-10 object-cover rounded flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="font-medium text-sm truncate" title={product.name}>{product.name}</p>
                          <p className="text-xs text-gray-500">
                            <span className="text-gray-400">{product.stock_code || '—'}</span>
                            <span className="mx-1">•</span>
                            R{product.price}{product.unit ? `/${product.unit}` : ''}
                          </p>
                        </div>
                      </div>

                      {/* Remaining columns flex container */}
                      <div className="flex-1 flex items-center gap-2">
                        {/* Combo Dropdown */}
                        <div className="flex-1">
                          <select
                            value={config.comboId || ''}
                            onChange={(e) => updateConfig(product.id, 'comboId', e.target.value || null)}
                            className={`w-full px-2 py-1.5 border rounded text-xs ${
                              config.comboId ? 'border-orange-400 bg-orange-50 text-orange-800 font-medium' : 'border-gray-200 text-gray-500'
                            }`}
                          >
                            <option value="">— None —</option>
                            {combos.map(combo => (
                              <option key={combo.id} value={combo.id}>
                                {combo.name} ({combo.quantity} for R{combo.price})
                              </option>
                            ))}
                          </select>
                        </div>

                        {/* Sale Price */}
                        <div className="w-28">
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">R</span>
                            <input
                              type="text" inputMode="decimal" value={config.salePrice || ''} placeholder="—"
                              onChange={(e) => updateConfig(product.id, 'salePrice', e.target.value)}
                              className={`w-full pl-5 pr-1 py-1.5 border rounded text-sm text-center ${
                                config.salePrice ? 'border-green-400 bg-green-50' : 'border-gray-200'
                              }`}
                            />
                          </div>
                          {savings > 0 && <p className="text-[10px] text-green-600 text-center mt-0.5">-R{savings.toFixed(2)}</p>}
                        </div>

                        {/* Cashback */}
                        <div className="w-32">
                          <div className="relative">
                            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">R</span>
                            <input
                              type="text" inputMode="decimal" value={config.cashback || ''} placeholder="—"
                              onChange={(e) => updateConfig(product.id, 'cashback', e.target.value)}
                              className={`w-full pl-5 pr-1 py-1.5 border rounded text-sm text-center ${
                                config.cashback ? 'border-purple-400 bg-purple-50' : 'border-gray-200'
                              }`}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            <div className="p-2 bg-gray-100 text-center text-xs text-gray-500 border-t shrink-0">
              {filteredProducts.length} of {products.length} products
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t flex justify-between shrink-0">
          <button onClick={onClose} className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 px-8 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50">
            {saving ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"/> : <Save size={20} />}
            {saving ? 'Saving...' : (promotion ? 'Update' : 'Create')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ========================================
// MAIN MODAL ROUTER
// ========================================
const PromotionModal = ({ promotion, products, categories, variantFamilies, onClose, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  
  const [formData, setFormData] = useState({
    name: promotion?.name || '',
    description: promotion?.description || '',
    applies_to: promotion?.applies_to || 'specific',
    category_filter: promotion?.category_filter || null,
    variant_family_id: promotion?.variant_family_id || null,
    start_date: promotion?.start_date?.split('T')[0] || new Date().toISOString().split('T')[0],
    end_date: promotion?.end_date?.split('T')[0] || new Date(Date.now() + 7*24*60*60*1000).toISOString().split('T')[0],
    markdown_price: promotion?.markdown_price || '',
    combo_quantity: promotion?.combo_quantity || 3,
    combo_price: promotion?.combo_price || '',
    combo_same_item: promotion?.combo_same_item ?? false,
  });

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productDiscounts, setProductDiscounts] = useState({});
  const [walletCredits, setWalletCredits] = useState({});

  // Initialize products on edit - MUST be before any conditional returns
  useEffect(() => {
    if (promotion?.product_ids && products.length > 0) {
      let ids = typeof promotion.product_ids === 'string' ? JSON.parse(promotion.product_ids) : promotion.product_ids;
      const selected = products.filter(p => ids.map(String).includes(String(p.id)));
      setSelectedProducts(selected);
      
      if (promotion.product_discounts) {
        const discounts = typeof promotion.product_discounts === 'string' 
          ? JSON.parse(promotion.product_discounts) : promotion.product_discounts;
        setProductDiscounts(discounts);
      }
      
      if (promotion.wallet_credit_amounts) {
        const credits = typeof promotion.wallet_credit_amounts === 'string'
          ? JSON.parse(promotion.wallet_credit_amounts) : promotion.wallet_credit_amounts;
        setWalletCredits(credits);
      }
    }
  }, [promotion, products]);

  // Route to All-In-One modal - AFTER all hooks
  if (formData.applies_to === 'all_in_one' || promotion?.applies_to === 'all_in_one') {
    return <AllInOneModal promotion={promotion} products={products} variantFamilies={variantFamilies} onClose={onClose} onSuccess={onSuccess} />;
  }

  const toggleProduct = (product) => {
    setSelectedProducts(prev => {
      const exists = prev.find(p => p.id === product.id);
      if (exists) return prev.filter(p => p.id !== product.id);
      return [...prev, product];
    });
  };

  const filteredProducts = products.filter(p => {
    const term = search.toLowerCase();
    return !term || p.name?.toLowerCase().includes(term) || String(p.stock_code || '').toLowerCase().includes(term);
  });

  const handleSave = async () => {
    if (!formData.name.trim()) { alert('Enter promotion name'); return; }
    
    if (['specific', 'combo', 'credit_wallet'].includes(formData.applies_to) && selectedProducts.length === 0) {
      alert('Select at least one product'); return;
    }
    
    if (formData.applies_to === 'combo' && (!formData.combo_price || parseFloat(formData.combo_price) <= 0)) {
      alert('Enter valid combo price'); return;
    }

    setSaving(true);
    try {
      const promoData = {
        ...formData,
        is_active: true,
        discount_type: 'markdown',
        product_ids: ['specific', 'combo', 'credit_wallet'].includes(formData.applies_to) ? selectedProducts.map(p => p.id) : null,
        product_discounts: formData.applies_to === 'specific' ? productDiscounts : null,
        wallet_credit_amounts: formData.applies_to === 'credit_wallet' ? walletCredits : null,
        combo_quantity: formData.applies_to === 'combo' ? parseInt(formData.combo_quantity) : null,
        combo_price: formData.applies_to === 'combo' ? parseFloat(formData.combo_price) : null,
        combo_same_item: formData.applies_to === 'combo' ? formData.combo_same_item : null,
        markdown_price: ['all', 'category'].includes(formData.applies_to) ? parseFloat(formData.markdown_price) : null,
        updated_at: new Date().toISOString(),
      };

      if (promotion) {
        await supabase.from('promotions_deals').update(promoData).eq('id', promotion.id);
      } else {
        await supabase.from('promotions_deals').insert([promoData]);
      }
      
      alert('✅ Saved!');
      onSuccess();
    } catch (error) {
      alert('Failed: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const getSteps = () => {
    if (['specific', 'combo', 'credit_wallet'].includes(formData.applies_to)) {
      return ['Details', 'Products', formData.applies_to === 'combo' ? 'Bundle' : 'Prices'];
    }
    if (formData.applies_to === 'category') return ['Details', 'Category', 'Price'];
    if (formData.applies_to === 'variant_family') return ['Details', 'Family', 'Price'];
    return ['Details', 'Scope', 'Price'];
  };

  const steps = getSteps();

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-5 flex justify-between shrink-0">
          <div>
            <h2 className="text-xl font-bold">{promotion ? 'Edit' : 'Create'} Promotion</h2>
            <p className="text-green-100 text-sm">{steps[step - 1]}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg">
            <X size={24} />
          </button>
        </div>

        {/* Steps */}
        <div className="bg-gray-50 px-6 py-3 flex items-center justify-center gap-4 border-b shrink-0">
          {steps.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                step > i + 1 ? 'bg-green-500 text-white' : step === i + 1 ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {step > i + 1 ? <Check size={16}/> : i + 1}
              </div>
              <span className={`text-sm font-medium ${step >= i + 1 ? 'text-green-600' : 'text-gray-400'}`}>{s}</span>
              {i < steps.length - 1 && <div className={`w-8 h-0.5 ${step > i + 1 ? 'bg-green-500' : 'bg-gray-200'}`}/>}
            </div>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          
          {/* Step 1: Details */}
          {step === 1 && (
            <div className="max-w-lg mx-auto space-y-4">
              <div>
                <label className="block text-sm font-bold mb-1">Name *</label>
                <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Super Sunday" className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg" />
              </div>
              
              <div>
                <label className="block text-sm font-bold mb-1">Description</label>
                <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Optional" rows={2} className="w-full px-4 py-2.5 border-2 border-gray-300 rounded-lg resize-none" />
              </div>

              <div>
                <label className="block text-sm font-bold mb-2">Type *</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'all_in_one', icon: Sparkles, label: 'All-In-One', desc: 'Combo + Markdown + Cashback', featured: true },
                    { key: 'specific', icon: Package, label: 'Specific Products', desc: 'Pick products with sale prices' },
                    { key: 'combo', icon: Box, label: 'Combo Bundle', desc: 'Buy X for fixed price', color: 'orange' },
                    { key: 'credit_wallet', icon: Wallet, label: 'Cashback', desc: 'Earn wallet credit', color: 'purple' },
                    { key: 'category', icon: ShoppingBag, label: 'Category', desc: 'All in category' },
                    { key: 'all', icon: Grid3x3, label: 'All Products', desc: 'Entire store' },
                  ].map(opt => (
                    <button key={opt.key} type="button" onClick={() => setFormData({...formData, applies_to: opt.key})}
                      className={`p-3 border-2 rounded-lg text-left transition-all ${opt.featured ? 'col-span-2' : ''} ${
                        formData.applies_to === opt.key
                          ? opt.featured ? 'border-purple-500 bg-purple-50' 
                            : opt.color === 'orange' ? 'border-orange-500 bg-orange-50'
                            : opt.color === 'purple' ? 'border-purple-500 bg-purple-50'
                            : 'border-green-500 bg-green-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}>
                      <div className="flex items-center gap-2">
                        <opt.icon size={18} className={formData.applies_to === opt.key ? 
                          (opt.featured || opt.color === 'purple' ? 'text-purple-600' : opt.color === 'orange' ? 'text-orange-600' : 'text-green-600') 
                          : 'text-gray-400'} />
                        <span className="font-semibold">{opt.label}</span>
                        {opt.featured && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">BEST</span>}
                      </div>
                      <p className="text-xs text-gray-500 mt-1 ml-6">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-bold mb-1">Start</label>
                  <input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg" />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1">End</label>
                  <input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                    className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg" />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Products/Category/etc */}
          {step === 2 && ['specific', 'combo', 'credit_wallet'].includes(formData.applies_to) && (
            <div className="space-y-4">
              {selectedProducts.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-green-800">{selectedProducts.length} selected</span>
                    <button onClick={() => setSelectedProducts([])} className="text-xs text-red-600 hover:underline">Clear</button>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {selectedProducts.slice(0,8).map(p => (
                      <span key={p.id} className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full flex items-center gap-1">
                        {p.name.slice(0,20)}{p.name.length > 20 ? '...' : ''}
                        <button onClick={() => toggleProduct(p)}><X size={12}/></button>
                      </span>
                    ))}
                    {selectedProducts.length > 8 && <span className="text-xs text-gray-500">+{selectedProducts.length - 8} more</span>}
                  </div>
                </div>
              )}
              
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search products..." className="w-full pl-10 py-2.5 border-2 border-gray-300 rounded-lg" />
              </div>
              
              <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                {filteredProducts.slice(0, 100).map(product => {
                  const isSelected = selectedProducts.find(p => p.id === product.id);
                  return (
                    <div key={product.id} onClick={() => toggleProduct(product)}
                      className={`border-2 rounded-lg p-2 cursor-pointer transition-all ${
                        isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'
                      }`}>
                      {isSelected && <div className="absolute top-1 right-1 bg-green-500 text-white rounded-full p-0.5"><Check size={10}/></div>}
                      <img src={getImageUrl(product.image)} alt="" className="w-full h-14 object-cover rounded mb-1" />
                      <p className="text-xs font-medium truncate">{product.name}</p>
                      <p className="text-xs text-green-600 font-bold">R{product.price}</p>
                    </div>
                  );
                })}
              </div>
              <p className="text-center text-xs text-gray-500">{filteredProducts.length} products</p>
            </div>
          )}

          {step === 2 && formData.applies_to === 'category' && (
            <div className="grid grid-cols-3 gap-3 max-w-2xl mx-auto">
              {categories.map(cat => (
                <button key={cat.id} onClick={() => setFormData({...formData, category_filter: cat.name})}
                  className={`p-4 border-2 rounded-lg text-left ${
                    formData.category_filter === cat.name ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-green-300'
                  }`}>
                  <ShoppingBag size={20} className={formData.category_filter === cat.name ? 'text-green-600' : 'text-gray-400'} />
                  <span className="block font-medium mt-2">{cat.name}</span>
                </button>
              ))}
            </div>
          )}

          {/* Step 3: Pricing */}
          {step === 3 && formData.applies_to === 'combo' && (
            <div className="max-w-md mx-auto bg-orange-50 border border-orange-200 rounded-lg p-6 space-y-4">
              <h3 className="font-bold text-orange-800 flex items-center gap-2"><Box size={20}/> Bundle Settings</h3>
              
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setFormData({...formData, combo_same_item: true})}
                  className={`p-3 border-2 rounded-lg ${formData.combo_same_item ? 'border-orange-500 bg-orange-100' : 'border-gray-300'}`}>
                  <span className="font-bold text-sm">Same Item</span>
                  <p className="text-xs text-gray-500">Must buy same product</p>
                </button>
                <button onClick={() => setFormData({...formData, combo_same_item: false})}
                  className={`p-3 border-2 rounded-lg ${!formData.combo_same_item ? 'border-orange-500 bg-orange-100' : 'border-gray-300'}`}>
                  <span className="font-bold text-sm">Mix & Match</span>
                  <p className="text-xs text-gray-500">Any combination</p>
                </button>
              </div>

              <div className="flex items-center gap-3">
                <span>Buy</span>
                <input type="number" min="2" value={formData.combo_quantity}
                  onChange={(e) => setFormData({...formData, combo_quantity: parseInt(e.target.value) || 2})}
                  className="w-16 px-2 py-2 border-2 rounded text-center font-bold text-lg" />
                <span>for</span>
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-bold">R</span>
                  <input type="text" inputMode="decimal" value={formData.combo_price}
                    onChange={(e) => setFormData({...formData, combo_price: e.target.value})}
                    placeholder="0.00" className="w-full pl-8 py-2 border-2 rounded font-bold text-lg" />
                </div>
              </div>
            </div>
          )}

          {step === 3 && formData.applies_to === 'specific' && (
            <div className="space-y-3">
              {selectedProducts.map(product => {
                const discount = productDiscounts[product.id] || {};
                return (
                  <div key={product.id} className="flex items-center gap-4 bg-white border rounded-lg p-3">
                    <img src={getImageUrl(product.image)} alt="" className="w-14 h-14 object-cover rounded" />
                    <div className="flex-1">
                      <p className="font-medium">{product.name}</p>
                      <p className="text-sm text-gray-500">Original: R{product.price}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold">Sale:</span>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 -translate-y-1/2">R</span>
                        <input type="text" inputMode="decimal" value={discount.markdown_price || ''}
                          onChange={(e) => setProductDiscounts({...productDiscounts, [product.id]: {markdown_price: e.target.value}})}
                          placeholder="0" className="w-24 pl-6 py-1.5 border-2 rounded" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {step === 3 && formData.applies_to === 'credit_wallet' && (
            <div className="space-y-3">
              {selectedProducts.map(product => (
                <div key={product.id} className="flex items-center gap-4 bg-white border rounded-lg p-3">
                  <img src={getImageUrl(product.image)} alt="" className="w-14 h-14 object-cover rounded" />
                  <div className="flex-1">
                    <p className="font-medium">{product.name}</p>
                    <p className="text-sm text-gray-500">Price: R{product.price}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-bold">Cashback:</span>
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2">R</span>
                      <input type="text" inputMode="decimal" value={walletCredits[product.id] || ''}
                        onChange={(e) => setWalletCredits({...walletCredits, [product.id]: e.target.value})}
                        placeholder="0" className="w-24 pl-6 py-1.5 border-2 rounded" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {step === 3 && ['all', 'category'].includes(formData.applies_to) && (
            <div className="max-w-sm mx-auto bg-green-50 border border-green-200 rounded-lg p-6">
              <h3 className="font-bold mb-3">Sale Price</h3>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 font-bold text-lg">R</span>
                <input type="text" inputMode="decimal" value={formData.markdown_price}
                  onChange={(e) => setFormData({...formData, markdown_price: e.target.value})}
                  placeholder="0.00" className="w-full pl-10 py-3 border-2 rounded-lg text-xl font-bold" />
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Applies to {formData.applies_to === 'all' ? 'all products' : `all in ${formData.category_filter}`}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 border-t flex justify-between shrink-0">
          <button onClick={() => step > 1 ? setStep(step - 1) : onClose}
            className="px-5 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100">
            {step > 1 ? '← Back' : 'Cancel'}
          </button>
          
          {step < steps.length ? (
            <button onClick={() => {
              if (step === 1 && !formData.name.trim()) { alert('Enter name'); return; }
              if (step === 2 && ['specific','combo','credit_wallet'].includes(formData.applies_to) && selectedProducts.length === 0) {
                alert('Select products'); return;
              }
              setStep(step + 1);
            }} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              Next →
            </button>
          ) : (
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
              {saving ? <div className="animate-spin h-5 w-5 border-2 border-white rounded-full border-t-transparent"/> : <Save size={18}/>}
              {saving ? 'Saving...' : 'Save'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default PromotionsAdmin;
